import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { WidgetParent } from "../../class/widget-parent";
import { ComponentAdapterCounterHotWater } from "../../class/adapters/component-adapter-counter-hotwater";
import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";
import { IWidgetExpandable } from "../../interfaces/IWidgetExpandable";
import { CounterHeaderData } from "../../class/counter-header-data";
import { WaterCounterMeasurementType } from "../../enum/enumCounterMeasurementType";
import { DateHelper } from "../../class/general-classes/date-helper";
import { SmartPaletteCounter } from "../../class/smart-palette";
import { CounterHeaderComponent } from "../counter/counter-header/counter-header.component";
import { CounterHotWaterComponent } from "../counter/counter-hot-water/counter-hot-water.component";
import { IonIcon } from "@ionic/angular";

@Component({
    selector: "app-widget-counter-hotwater",
    templateUrl: "./widget-counter-hotwater.component.html",
    styleUrls: [
        "./widget-counter-hotwater.component.scss",
        "../../styles/shared-global.scss",
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CounterHeaderComponent, CounterHotWaterComponent, IonIcon]
})
export class WidgetCounterHotwaterComponent
  extends WidgetParent<ComponentAdapterCounterHotWater, SmartPaletteCounter>
  implements IWidgetExpandable
{
  public isExpanded: boolean = false;
  public areChartsVisible: boolean = false;
  public headerData: CounterHeaderData = null;

  private _currentMonth: string = "";
  private _previousMonth: string = "";

  /**
   * Open or close the widget
   */
  public onClickOpenCloseWidget(): void {
    if (!this.isExpanded) {
      this.onExpandWidget();
    } else {
      this.onCollapseWidget();
    }
  }

  onExpandWidget(): void {
    this.isExpanded = true;
  }

  onCollapseWidget(): void {
    this.isExpanded = false;
  }
  protected override onLanguageChange(): void {
    const now = new Date();
    this._currentMonth = this._localeService.formatDate(now, "month").toUpperCase();
    this._previousMonth = this._localeService.formatDate(DateHelper.subtract(now, 1, "month"), "month").toUpperCase();

    if (this.headerData) {
      this.headerData.subtitle = this._currentMonth;
      this.headerData.title1 = this._translateService.instant("dictionary.flowRate");
      this.headerData.title2 = this._translateService.instant("dictionary.volume");
    }

    if (this.componentAdapter) {
      this.componentAdapter.CurrentMonth = this._currentMonth;
      this.componentAdapter.PreviousMonth = this._previousMonth;
    }
  }

  protected setWidgetSmartPalette(): SmartPaletteCounter {
    // All counters share the same brand-derived palette (see setWidgetSmartPaletteCounterDefault).
    return this.setWidgetSmartPaletteCounterDefault();
  }

  protected onComponentLoad(): void {}

  protected onSmartComponentSuccessLoaded(): void {
    const factory = new ComponentAdapterFactory();
    this.loadSummaryData();
    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdapterCounterHotWater,
      this._headerSummary
    );
    this.componentAdapter.fillMeasurementMap(
      this._listSmartComponentCounterMeasurements,
      WaterCounterMeasurementType,
      this.defaultDecimalDigits,
      this._localeService.locale()
    );

    this.prepareHeaderData();
  }

  /**
   * This method prepare and initialise the data passed to the header compoent. These data are:
   * + Taken from the previously fetched property.set of the SmartComponent (Name)
   * + Defined locally as localised text strings based on the counter sub-type (Titles)
   * + Calculated locally (Subtitle)
   */
  private prepareHeaderData(): void | boolean {
    const dataToHeader: CounterHeaderData = new CounterHeaderData();

    if (this.smartComponent.summary) {
      dataToHeader.titleWidget = this._translateService.instant("dictionary.consumptionDistribution");
      dataToHeader.subtitle = "";
    } else {
      dataToHeader.titleWidget = this.componentAdapter.Name;
      dataToHeader.subtitle = this._currentMonth;
    }

    //If component title is empty show error loading component
    if (!dataToHeader.titleWidget) {
      return (this._errorLoading = true);
    }

    if (this.componentAdapter.Volume) {
      dataToHeader.title1 = this._translateService.instant("dictionary.volume");
      dataToHeader.value1 = this.componentAdapter.MeasurementMap.get(
        WaterCounterMeasurementType[
          WaterCounterMeasurementType.WATER_VOLUME_LAST_MONTH
        ]
      );
      if (!dataToHeader.value1) {
        dataToHeader.value1 = "---";
      }
    }
    if (this.componentAdapter.Flow && !this.componentAdapter.Summary) {
      dataToHeader.title2 = this._translateService.instant("dictionary.flowRate");
      dataToHeader.value2 = this.componentAdapter.MeasurementMap.get(
        WaterCounterMeasurementType[WaterCounterMeasurementType.WATER_FLOW]
      );

      if (!dataToHeader.value2) {
        dataToHeader.value2 = "---";
      }
    }

    this.headerData = dataToHeader;

    this.isExpanded = this._isExpanded;
    this.areChartsVisible = this._isExpanded ? true : false;
  }

  protected onComponentDestroy(): void {}

  protected onSmartComponentErrorLoading(): void {}

  protected onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {}
}
